import React, { Fragment, useContext, useEffect, useState } from 'react';
import { Card, CardHeader, Col, Input, Label, Media, Table, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useLoader } from '../../../_helper/LoaderContext';
import { Breadcrumbs, H5, ToolTip } from '../../../AbstractElements';
import { useNavigate } from 'react-router';
import { getStaffShiftHistory } from '../service/manage-staff-service';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import MyPagination from '../../pagination';

const ShiftRecordList = () => {
    const { t } = useTranslation();
    const { setLoading } = useLoader();
    const [totalItems, setTotalItems] = useState(0);
    const [totalUserCount, setTotalUserCount] = useState(0);
    const [listData, setList] = useState([]);
    const [skeletonLoading, setSkeletonLoading] = useState(true);
    const [viewTooltipOpen, setViewTooltipOpen] = useState(false);

    const [page, setPage] = useState(1);
    const [size, setSize] = useState(10);
    const history = useNavigate();
    const [searchValue, setSearchValue] = useState('');
    const toggleViewTooltip = () => setViewTooltipOpen(!viewTooltipOpen);
    const [viewView, setViewView] = useState(true);

    useEffect(() => {
        // Fetch List data when component mounts
        getList();
    }, [page, size]);

    const getList = async () => {
        const body = {
            page: page,
            pagelimitSize: size,
            search: searchValue.trim(),
            // role: ''
        }
        try {
            setLoading(true);
            setSkeletonLoading(true);
            const response = await getStaffShiftHistory(body)
            if (response.data.status === 'success') {
                // Updated to use pharmacyUserList from API response
                setList(response.data.data.pharmacyUserList || []);
                const totalCount = response.data.data.totalPharmacyUser;
                setTotalUserCount(totalCount)
                const totalPagesItems = Math.ceil(totalCount / size);
                setTotalItems(totalPagesItems);
                setLoading(false);
                setSkeletonLoading(false);
            }
        } catch (error) {
            setLoading(false);
            setSkeletonLoading(false);
            console.error('Error fetching data:', error);
        }
    };

    // Format date to readable format
    // const formatDate = (dateString) => {
    //     const date = new Date(dateString);
    //     return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    // };
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
        });
    };


    // Calculate shift duration
    const calculateDuration = (startTime, endTime) => {
        if (!startTime) return 'N/A';

        const start = new Date(startTime);
        const end = endTime ? new Date(endTime) : new Date();

        const diffMs = end - start;
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

        return `${diffHours}h ${diffMinutes}m`;
    };

    // Format currency amounts
    const formatCurrency = (amountObj) => {
        if (!amountObj || Object.keys(amountObj)?.length === 0) return 'N/A';

        return Object.entries(amountObj).map(([currency, amount]) => (
            <div key={currency} style={{ marginBottom: '2px' }}>
                <strong>{currency}:</strong> {amount.toFixed(2)}
            </div>
        ));
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    // Handle page size change
    const handlePageSizeChange = (e) => {
        setSize(Number(e.target.value));
        setPage(1); // Reset to first page
    };

    // Event handler for opening view component
    const handleViewClick = (getID) => {
        // console.log(getID);
        history(`${process.env.PUBLIC_URL}/shift-record/detail/${getID}`);
    };

    return (
        <Fragment>
            <Breadcrumbs mainTitle={t('Staff Shift Record')} parent={t('Dashboard')} title={t('Staff Shift Record')} />
            <Col sm="12">
                <Card>
                    <CardHeader className="d-flex justify-content-between align-items-center">
                        <H5>{t('Shift History')}</H5>
                    </CardHeader>

                    <div className="card-block">
                        <div className='row' style={{ padding: "12px 24px" }}>
                            <Col sm="12" lg="12" xl="12">
                                <div className="table-responsive rounded">
                                    <Table className='table-center table-bordered mb-0 table-hover'>
                                        <thead>
                                            <tr>
                                                <th className="border-bottom p-3" width="3%">#</th>
                                                <th className="border-bottom p-3" width="10%">{t("Full Name")}</th>
                                                <th className="border-bottom p-3" width="10%">{t("Shift Status")}</th>
                                                <th className="border-bottom p-3" width="15%">{t("Start Time")}</th>
                                                <th className="border-bottom p-3" width="15%">{t("End Time")}</th>
                                                <th className="border-bottom p-3" width="10%">{t("Duration")}</th>
                                                <th className="border-bottom p-3" width="15%">{t("Opening Amount")}</th>
                                                <th className="border-bottom p-3" width="15%">{t("Closing Amount")}</th>
                                                <th className="border-bottom p-3" width="8%">{t("Sales Count")}</th>
                                                <th className="border-bottom p-3" width="9%">{t("Refund Count")}</th>
                                                <th className="border-bottom p-3" width="9%">{t("Action")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {skeletonLoading ? (
                                                // Skeleton rows
                                                [...Array(5)].map((_, index) => (
                                                    <tr key={index}>
                                                        <td><Skeleton height={20} /></td>
                                                        <td><Skeleton height={20} width="80%" /></td>
                                                        <td><Skeleton height={20} width="80%" /></td>
                                                        <td><Skeleton height={20} width="80%" /></td>
                                                        <td><Skeleton height={20} width="80%" /></td>
                                                        <td><Skeleton height={20} width="60%" /></td>
                                                        <td><Skeleton height={40} width="90%" /></td>
                                                        <td><Skeleton height={40} width="90%" /></td>
                                                        <td><Skeleton height={20} width="50%" /></td>
                                                        <td><Skeleton height={20} width="50%" /></td>
                                                        <td className='text-center' style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                                                            <Skeleton circle height={35} width={35} />
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : listData.length > 0 ? (
                                                listData.map((item, index) => {
                                                    const cashierName = item.cashierInfo ? item.cashierInfo[0].contact_person.full_name || item.cashierInfo[0].contact_person.first_name+" "+item.cashierInfo[0].contact_person.last_name  : '-'
                                                    return (
                                                        <tr key={item._id || index}>
                                                            <td>{(page - 1) * size + index + 1}</td>
                                                            <td>{cashierName}</td>
                                                            <td>
                                                                <span
                                                                    className={`badge ${item.status === 'running' ? 'bg-success' : 'bg-secondary'}`}
                                                                    style={{
                                                                        padding: '5px 10px',
                                                                        borderRadius: '4px',
                                                                        textTransform: 'capitalize'
                                                                    }}
                                                                >
                                                                    {item.status || 'N/A'}
                                                                </span>
                                                            </td>
                                                            <td>{formatDate(item.shiftStartTime)}</td>
                                                            <td>{item.shiftEndTime ? formatDate(item.shiftEndTime) : 'Still Running'}</td>
                                                            <td>{calculateDuration(item.shiftStartTime, item.shiftEndTime)}</td>
                                                            <td>{formatCurrency(item.openingAmount)}</td>
                                                            <td>{formatCurrency(item.closingAmount)}</td>
                                                            <td className="text-center fw-bold">
                                                                {/* <span className="badge bg-info" style={{ fontSize: '14px' }}>
                                                            </span> */}
                                                                {item.totalSalesCount || 0}
                                                            </td>
                                                            <td className="text-center fw-bold">
                                                                {/* <span className="badge bg-warning" style={{ fontSize: '14px' }}>
                                                            </span> */}
                                                                {item.totalRefundCount || 0}
                                                            </td>
                                                            <td className="text-center">
                                                                {viewView && (
                                                                    <button className='success-action-button' id="ViewToolTip" onClick={() => handleViewClick(item?._id)}>
                                                                        <i className="fa fa-eye" style={{ fontSize: "18px" }}></i>
                                                                        <ToolTip
                                                                            attrToolTip={{
                                                                                placement: 'top',
                                                                                isOpen: viewTooltipOpen,
                                                                                target: 'ViewToolTip',
                                                                                toggle: toggleViewTooltip,
                                                                            }} >
                                                                            <strong>{t("View")}</strong>
                                                                        </ToolTip>
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    )
                                                })
                                            ) : (
                                                <tr className="text-center">
                                                    <td colSpan="9">{t("No shift records available at this time.")}</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </Col>

                            {/* Pagination */}
                            <Col sm="12" lg="12" xl="12" className="mt-4">
                                <div className="d-md-flex align-items-center text-center justify-content-between">
                                    <div className="show-entries pt-3">
                                        <Label>{t("Show")}&nbsp;</Label>
                                        <select
                                            style={{
                                                display: 'unset',
                                                width: 'auto',
                                                padding: '0.375rem 2.25rem 0.375rem 0.75rem'
                                            }}
                                            value={size}
                                            onChange={handlePageSizeChange}
                                            className="form-control"
                                        >
                                            <option value={5}>5</option>
                                            <option value={10}>10</option>
                                            <option value={20}>20</option>
                                            <option value={30}>30</option>
                                            <option value={50}>50</option>
                                        </select>&nbsp;
                                        <Label>{t("entries")}</Label>
                                    </div>
                                    <MyPagination totalPages={totalItems} currentPage={page} onPageChange={handlePageChange} />
                                    {/* <div className="total-records pt-3">
                                        <Label>
                                            Total Records: {totalUserCount}
                                        </Label>
                                    </div> */}
                                </div>
                            </Col>
                        </div>
                    </div>
                </Card>
            </Col>
        </Fragment>
    )
}

export default ShiftRecordList;